import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { AlertTriangle, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  customerErrorCode,
  customerErrorDetails,
  customerErrorUsesDialog,
  type CustomerErrorDetails,
} from "@/lib/customer-error";
import { useI18n } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface CustomerErrorViewerProps {
  error?: unknown;
  details?: CustomerErrorDetails | null | undefined;
  title?: string | undefined;
  message?: string | undefined;
  actionLabel?: string | undefined;
  onAction?: (() => void) | undefined;
  actionDisabled?: boolean | undefined;
  onDismiss?: (() => void) | undefined;
  compact?: boolean | undefined;
  className?: string | undefined;
}

export function CustomerErrorViewer({
  error,
  details,
  title,
  message,
  actionLabel,
  onAction,
  actionDisabled = false,
  onDismiss,
  compact = false,
  className,
}: CustomerErrorViewerProps) {
  const { t } = useI18n();
  const content = details ?? customerErrorDetails(error, t);

  return (
    <section
      className={cn(
        "flex items-start gap-3 rounded-2xl border border-destructive/25 bg-destructive/5 text-start shadow-sm",
        compact ? "p-3" : "p-4 sm:p-5",
        className,
      )}
      role="alert"
      aria-atomic="true"
      data-error-code={content.code || undefined}
    >
      <span
        className={cn(
          "grid shrink-0 place-items-center rounded-full bg-destructive/10 text-destructive",
          compact ? "size-9" : "size-11",
        )}
      >
        <AlertTriangle
          className={compact ? "size-4" : "size-5"}
          aria-hidden="true"
        />
      </span>
      <div className="min-w-0 flex-1">
        <h2
          className={cn(
            "font-bold text-foreground",
            compact ? "text-sm" : "text-base",
          )}
        >
          {title ?? content.title}
        </h2>
        <p
          className={cn(
            "text-muted-foreground",
            compact ? "mt-0.5 text-xs" : "mt-1 text-sm",
          )}
        >
          {message ?? content.message}
        </p>
        {actionLabel && onAction ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="mt-3"
            disabled={actionDisabled}
            onClick={onAction}
          >
            {actionLabel}
          </Button>
        ) : null}
      </div>
      {onDismiss ? (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="-me-1 -mt-1 size-9 shrink-0"
          aria-label={t("common.close")}
          onClick={onDismiss}
        >
          <X className="size-4" aria-hidden="true" />
        </Button>
      ) : null}
    </section>
  );
}

interface GlobalErrorOptions {
  details?: CustomerErrorDetails | undefined;
  actionLabel?: string | undefined;
  onAction?: (() => void) | undefined;
}

interface GlobalErrorEntry extends GlobalErrorOptions {
  id: number;
  error: unknown;
}

interface CustomerErrorViewerContextValue {
  showError: (error: unknown, options?: GlobalErrorOptions) => void;
  dismissError: () => void;
}

const CustomerErrorViewerContext =
  createContext<CustomerErrorViewerContextValue | null>(null);

export function CustomerErrorViewerProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [entry, setEntry] = useState<GlobalErrorEntry | null>(null);
  const nextId = useRef(0);

  const dismissError = useCallback(() => setEntry(null), []);
  const showError = useCallback(
    (error: unknown, options: GlobalErrorOptions = {}) => {
      setEntry((current) => {
        const code = customerErrorCode(error);
        if (current && code && customerErrorCode(current.error) === code) {
          return { id: current.id, error, ...options };
        }
        nextId.current += 1;
        return { id: nextId.current, error, ...options };
      });
    },
    [],
  );
  const value = useMemo(
    () => ({ showError, dismissError }),
    [dismissError, showError],
  );

  return (
    <CustomerErrorViewerContext.Provider value={value}>
      {children}
      {entry ? (
        <CustomerErrorDialog
          key={entry.id}
          entry={entry}
          onDismiss={dismissError}
        />
      ) : null}
    </CustomerErrorViewerContext.Provider>
  );
}

function CustomerErrorDialog({
  entry,
  onDismiss,
}: {
  entry: GlobalErrorEntry;
  onDismiss: () => void;
}) {
  const { t } = useI18n();
  const details = entry.details ?? customerErrorDetails(entry.error, t);
  return (
    <Dialog open onOpenChange={(open) => !open && onDismiss()}>
      <DialogContent
        className="max-w-md overflow-hidden rounded-3xl border-gold/40 p-0 shadow-lift"
        data-error-code={details.code || undefined}
      >
        <div className="grid gap-5 p-6 pt-7 sm:p-7">
          <span className="grid size-14 place-items-center rounded-full bg-gold/20 text-gold-foreground">
            <AlertTriangle className="size-7" aria-hidden="true" />
          </span>
          <DialogHeader className="gap-2 pe-7">
            <DialogTitle className="text-xl font-black leading-tight">
              {details.title}
            </DialogTitle>
            <DialogDescription className="text-sm leading-6">
              {details.message}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:space-x-0">
            <DialogClose asChild>
              <Button variant={entry.onAction ? "outline" : "default"}>
                {t("common.close")}
              </Button>
            </DialogClose>
            {entry.actionLabel && entry.onAction ? (
              <Button
                type="button"
                onClick={() => {
                  const action = entry.onAction;
                  onDismiss();
                  action?.();
                }}
              >
                {entry.actionLabel}
              </Button>
            ) : null}
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function useCustomerErrorDialog(
  error: unknown,
  options?: GlobalErrorOptions,
): void {
  const context = useContext(CustomerErrorViewerContext);
  const optionsRef = useRef<GlobalErrorOptions>(options ?? {});
  optionsRef.current = options ?? {};

  useEffect(() => {
    if (!context || !error || !customerErrorUsesDialog(error)) return;
    context.showError(error, optionsRef.current);
  }, [context, error]);
}

export function useCustomerErrorViewer(): CustomerErrorViewerContextValue {
  const context = useContext(CustomerErrorViewerContext);
  if (!context) {
    throw new Error(
      "useCustomerErrorViewer must be used inside CustomerErrorViewerProvider",
    );
  }
  return context;
}
